<template>
    <div class="widget-card-edit">
        <RedesignBaseForm
            ref="editWidgetForm"
            :model="model"
            :show-message="false"
            class="widget-card-edit-form"
        >
            <BaseCollapse v-model="activeCollapse">
                <div v-if="currentFormComponent">
                    <component
                        :is="currentFormComponent"
                        v-model="widgetLayoutModel"
                        :title="model.Title"
                        :template-id="model.TemplateID"
                        :widget-config="model.WidgetConfig"
                        :widget-time="model.WidgetTime"
                        :load-entities-list="loadEntitiesList"
                        :simple-layout-options="isAiCounterWidgetType(model)"
                        @update:config="updateFilterItem"
                        @update:title="updateTitle"
                        @refreshEntitiesList="refreshEntitiesList"
                        @update:widgetTime="onUpdateTime"
                    />
                </div>
            </BaseCollapse>
        </RedesignBaseForm>
    </div>
</template>

<script setup lang="ts">
import RedesignBaseForm from '@/components/RedesignBaseComponents/Form/RedesignBaseForm.vue'
import BaseCollapse from '@/components/RedesignBaseComponents/Collapse/BaseCollapse.vue'
import { computed, onMounted, ref } from 'vue'
import { useVModel } from '@vueuse/core'
import { WidgetConfig as TWidgetConfig, WidgetExtended, WidgetTime as TWidgetTime } from '@/types/dashboard'
import {
    isAverageCallsDurationCard,
    isAiCounterWidgetType,
    isCounterAgentsInStatus,
    isQueueCounterWidgetType,
    isTotalOutgoingCallType,
    isVoicebotCounterWidgetType
} from '@/helpers/widgetUtils'
/* Forms */
import CounterAgentsInStatusForm
    from '@/components/RedesignComponents/Widgets/Edit/WidgetCardsForms/CounterAgentsInStatusForm.vue'
import AvgCallsDurationForm
    from '@/components/RedesignComponents/Widgets/Edit/WidgetCardsForms/AvgCallsDurationForm.vue'
import QueueCounterCardForm
    from '@/components/RedesignComponents/Widgets/Edit/WidgetCardsForms/QueueCounterCardForm.vue'
import TotalOutgoingCallForm
    from '@/components/RedesignComponents/Widgets/Edit/WidgetCardsForms/TotalOutgoingCallForm.vue'
import VoicebotCounterCardForm
    from '@/components/RedesignComponents/Widgets/Edit/WidgetCardsForms/VoicebotCounterCardForm.vue'
import cloneDeep from 'lodash/cloneDeep'
import { refreshEntitiesData } from '@/composables/globalData/useEntitiesData'
import { AVERAGE_DURATION_ALL_CALLS } from '@/enum/queueCounters'

const SHOW_COUNTER_DEFAULT_KEY = AVERAGE_DURATION_ALL_CALLS

const FORM_COMPONENTS = {
    AvgCallsDurationForm,
    CounterAgentsInStatusForm,
    QueueCounterCardForm,
    TotalOutgoingCallForm,
    VoicebotCounterCardForm
}

/* Props */
const props = defineProps<{
    value: WidgetExtended
}>()
/* Emits */
const emit = defineEmits([ 'update:value', 'input' ])

const activeCollapse = ref([ '' ])
const loadEntitiesList = ref(false)

const editWidgetForm = ref()

const model = useVModel(props, 'value', emit, {
    eventName: 'input',
    passive: true
})
const currentFormComponent = computed(() => {
    if (isCounterAgentsInStatus(model.value)) {
        return FORM_COMPONENTS.CounterAgentsInStatusForm
    }
    if (isAverageCallsDurationCard(model.value)) {
        return FORM_COMPONENTS.AvgCallsDurationForm
    }
    if (isQueueCounterWidgetType(model.value)) {
        return FORM_COMPONENTS.QueueCounterCardForm
    }
    if (isAiCounterWidgetType(model.value)) {
        return FORM_COMPONENTS.TotalOutgoingCallForm
    }
    if (isTotalOutgoingCallType(model.value)) {
        return FORM_COMPONENTS.TotalOutgoingCallForm
    }
    if (isVoicebotCounterWidgetType(model.value)) {
        return FORM_COMPONENTS.VoicebotCounterCardForm
    }

    return ''
})
const isWidgetAvgCallsDuration = computed(() => {
    return isAverageCallsDurationCard(model.value)
})

const widgetLayoutModel = computed({
    get () {
        return model.value.WidgetLayout
    },
    set (val) {
        model.value.WidgetLayout = { ...val }
    }
})

/* Methods */
function updateFilterItem (filterData: TWidgetConfig) {
    const _index = model.value.WidgetConfig?.findIndex(i => i.ParameterID === filterData.ParameterID)

    if (_index > -1) {
        const widgetConfigToUpdate = cloneDeep(model.value.WidgetConfig)

        widgetConfigToUpdate[_index] = filterData

        model.value = {
            ...model.value,
            WidgetConfig: widgetConfigToUpdate
        }
    }
}

async function refreshEntitiesList () {
    loadEntitiesList.value = true
    await refreshEntitiesData()
    loadEntitiesList.value = false
}

function setDefaultCounterType () {
    if (!widgetLayoutModel.value.showCounter) {
        widgetLayoutModel.value.showCounter = SHOW_COUNTER_DEFAULT_KEY
    }
}

function initData () {
    if (isWidgetAvgCallsDuration.value) {
        setDefaultCounterType()
    }
    widgetLayoutModel.value = {
        ...widgetLayoutModel.value,
        showText: props.value?.WidgetLayout.showText ?? true,
        displayBorder: props.value?.WidgetLayout.displayBorder ?? true
    }
}

function onUpdateTime (time: TWidgetTime) {
    model.value.WidgetTime = { ...time }
}

function updateTitle (title: string) {
    model.value.Title = title
}

async function validate () {
    const res = await editWidgetForm.value?.validate()
    return res?.isValid
}

defineExpose({
    validate
})

onMounted(initData)
</script>

